import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiHash,
  FiInfo,
  FiLink,
  FiNavigation,
  FiPlus,
  FiSave,
  FiSearch,
  FiShuffle,
  FiTrash2
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mockApi, type Link as LinkType } from "../api/mockApi";
import { ABTestDomainSelection } from "../components/CreateLink";

interface Variant {
  id: string;
  type: "path" | "variant";
  value: string;
  percentage: number;
}

// Available paths for dropdowns (same as CreateLink)
const availablePaths = [
  { value: "/buyer/home", label: "/buyer/home", description: "Buyer homepage" },
  {
    value: "/buyer/search",
    label: "/buyer/search",
    description: "Property search"
  },
  {
    value: "/buyer/favorites",
    label: "/buyer/favorites",
    description: "Saved properties"
  },
  {
    value: "/seller/home",
    label: "/seller/home",
    description: "Seller homepage"
  },
  {
    value: "/seller/valuation",
    label: "/seller/valuation",
    description: "Home valuation tool"
  },
  {
    value: "/seller/guide",
    label: "/seller/guide",
    description: "Selling guide"
  },
  {
    value: "/agent/dashboard",
    label: "/agent/dashboard",
    description: "Agent portal"
  },
  {
    value: "/agent/listings",
    label: "/agent/listings",
    description: "Manage listings"
  },
  { value: "/resources", label: "/resources", description: "Resources page" },
  { value: "/blog", label: "/blog", description: "Blog homepage" },
  { value: "/about", label: "/about", description: "About us" },
  { value: "/contact", label: "/contact", description: "Contact page" },
  { value: "/", label: "/", description: "Homepage" }
];

// Available website variants
const availableVariants = [
  {
    value: "54424264261",
    label: "54424264261",
    description: "Homepage - Version A"
  },
  {
    value: "54424264262",
    label: "54424264262",
    description: "Homepage - Version B"
  },
  {
    value: "1754424264261",
    label: "1754424264261",
    description: "Buyer Landing - V1"
  },
  {
    value: "1754424264262",
    label: "1754424264262",
    description: "Buyer Landing - V2"
  },
  {
    value: "2854424264261",
    label: "2854424264261",
    description: "Seller Landing - V1"
  },
  {
    value: "2854424264262",
    label: "2854424264262",
    description: "Seller Landing - V2"
  }
];

// Mock data for existing A/B test
const mockABTestData = {
  "test-001": {
    testName: "Homepage Hero Test",
    description: "Testing different hero sections for homepage conversion",
    isActive: true,
    domain: "zown.ca",
    trafficSource: "website-path" as const,
    selectedTrackingLinks: [],
    selectedWebsitePath: "/",
    schedule: {
      enabled: false,
      startDate: "",
      endDate: ""
    },
    variants: [
      { id: "1", type: "path" as const, value: "/buyer/home", percentage: 50 },
      {
        id: "2",
        type: "variant" as const,
        value: "54424264261",
        percentage: 50
      }
    ]
  },
  "test-002": {
    testName: "Email Campaign Test",
    description: "Testing email campaign landing pages",
    isActive: false,
    domain: "properties.zown.ca",
    trafficSource: "tracking-link" as const,
    selectedTrackingLinks: [],
    selectedWebsitePath: "",
    schedule: {
      enabled: true,
      startDate: "2024-01-15T09:00",
      endDate: "2024-02-15T17:00"
    },
    variants: [
      { id: "1", type: "path" as const, value: "/seller/home", percentage: 33 },
      {
        id: "2",
        type: "path" as const,
        value: "/seller/guide",
        percentage: 33
      },
      {
        id: "3",
        type: "variant" as const,
        value: "2854424264261",
        percentage: 34
      }
    ]
  }
};

function EditABTest() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Fetch tracking links for the dropdown
  const { data: trackingLinks = [], isLoading: isLoadingLinks } = useQuery({
    queryKey: ["links"],
    queryFn: mockApi.getLinks
  });

  const [testName, setTestName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [domain, setDomain] = useState("zown.ca");
  const [trafficSource, setTrafficSource] = useState<
    "tracking-link" | "website-path"
  >("website-path");
  const [selectedTrackingLinks, setSelectedTrackingLinks] = useState<
    LinkType[]
  >([]);
  const [selectedWebsitePath, setSelectedWebsitePath] = useState("");
  const [schedule, setSchedule] = useState({
    enabled: false,
    startDate: "",
    endDate: ""
  });

  // Helper function to get current datetime string for input
  const getCurrentDateTimeString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  const [variants, setVariants] = useState<Variant[]>([
    { id: "1", type: "path", value: "", percentage: 50 },
    { id: "2", type: "path", value: "", percentage: 50 }
  ]);
  const [dropdownStates, setDropdownStates] = useState<{
    [key: string]: { isOpen: boolean; searchQuery: string };
  }>({});
  const [trafficSourceDropdown, setTrafficSourceDropdown] = useState({
    isOpen: false,
    searchQuery: ""
  });
  const [websitePathDropdown, setWebsitePathDropdown] = useState({
    isOpen: false,
    searchQuery: ""
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load existing test data on component mount
  useEffect(() => {
    const loadTestData = async () => {
      setIsLoading(true);
      // Simulate loading existing test data
      setTimeout(() => {
        const testData = mockABTestData[id as keyof typeof mockABTestData];
        if (testData) {
          setTestName(testData.testName);
          setDescription(testData.description);
          setIsActive(testData.isActive);
          setDomain(testData.domain);
          setTrafficSource(testData.trafficSource);
          setSelectedWebsitePath(testData.selectedWebsitePath);
          setSchedule(testData.schedule);
          setVariants(testData.variants);

          // For tracking links, we'd normally load them from the test data
          // For now, we'll use the first few links from the available list as an example
          if (testData.trafficSource === "tracking-link") {
            // Mock: select first 2 tracking links
            trackingLinks.length > 0 &&
              setSelectedTrackingLinks(trackingLinks.slice(0, 2));
          }
        }
        setIsLoading(false);
      }, 500);
    };

    loadTestData();
  }, [id, trackingLinks]);

  // Get main domain from selected domain
  const getMainDomain = (mainDomain: string) => {
    return mainDomain; // Now we're using main domains directly
  };

  // Toggle tracking link selection
  const toggleTrackingLinkSelection = (link: LinkType) => {
    setSelectedTrackingLinks((prev) => {
      const isSelected = prev.some((selected) => selected.id === link.id);
      if (isSelected) {
        return prev.filter((selected) => selected.id !== link.id);
      } else {
        return [...prev, link];
      }
    });
  };

  const addVariant = () => {
    const newVariant = {
      id: Date.now().toString(),
      type: "path" as const,
      value: "",
      percentage: 0
    };
    const updatedVariants = [...variants, newVariant];
    const redistributedVariants = updatedVariants.map((v) => ({
      ...v,
      percentage: Math.floor(100 / updatedVariants.length)
    }));
    setVariants(redistributedVariants);
  };

  const removeVariant = (variantId: string) => {
    if (variants.length <= 2) return;
    const updatedVariants = variants.filter((v) => v.id !== variantId);
    const redistributedVariants = updatedVariants.map((v) => ({
      ...v,
      percentage: Math.floor(100 / updatedVariants.length)
    }));
    setVariants(redistributedVariants);
  };

  const updateVariant = (index: number, updates: Partial<Variant>) => {
    const updatedVariants = [...variants];
    updatedVariants[index] = { ...updatedVariants[index], ...updates };
    setVariants(updatedVariants);
  };

  const distributeEvenly = () => {
    const equalPercentage = Math.floor(100 / variants.length);
    const remainder = 100 % variants.length;
    const redistributedVariants = variants.map((v, i) => ({
      ...v,
      percentage: i === 0 ? equalPercentage + remainder : equalPercentage
    }));
    setVariants(redistributedVariants);
  };

  const handleSave = () => {
    // Here you would normally save to the API
    console.log("Updating A/B Test:", {
      id,
      testName,
      description,
      isActive,
      domain,
      trafficSource,
      selectedTrackingLinks,
      selectedWebsitePath,
      schedule,
      variants
    });
    navigate("/website");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Handle traffic source dropdown
      if (
        trafficSourceDropdown.isOpen &&
        !target.closest("[data-traffic-source-dropdown]")
      ) {
        setTrafficSourceDropdown({ isOpen: false, searchQuery: "" });
      }

      // Handle website path dropdown
      if (
        websitePathDropdown.isOpen &&
        !target.closest("[data-website-path-dropdown]")
      ) {
        setWebsitePathDropdown({ isOpen: false, searchQuery: "" });
      }

      // Handle variant dropdowns
      Object.entries(dropdownStates).forEach(([variantId, dropdown]) => {
        if (
          dropdown.isOpen &&
          !target.closest(`[data-ab-dropdown="${variantId}"]`)
        ) {
          setDropdownStates((prev) => ({
            ...prev,
            [variantId]: { ...prev[variantId], isOpen: false, searchQuery: "" }
          }));
        }
      });
    };

    const hasOpenDropdowns =
      trafficSourceDropdown.isOpen ||
      websitePathDropdown.isOpen ||
      Object.values(dropdownStates).some((d) => d.isOpen);
    if (hasOpenDropdowns) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [dropdownStates, trafficSourceDropdown, websitePathDropdown]);

  if (isLoading) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Container maxW="6xl" py={8}>
          <VStack gap={6} align="center" justify="center" minH="60vh">
            <Text fontSize="lg" color="gray.600">
              Loading A/B Test...
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="6xl" py={8}>
        {/* Header */}
        <VStack gap={6} align="stretch" mb={8}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <Link to="/website">
                <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                  <FiArrowLeft />
                  Back to Website
                </Button>
              </Link>
              <Text color="gray.500">/</Text>
              <Text color="gray.700" fontWeight="medium">
                Edit A/B Test
              </Text>
              <Badge colorScheme="blue">ID: {id}</Badge>
            </HStack>
            <HStack gap={3}>
              <HStack>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  Active
                </Text>
                <Box
                  position="relative"
                  display="inline-block"
                  width="44px"
                  height="24px"
                  bg={isActive ? "green.500" : "gray.300"}
                  borderRadius="full"
                  transition="all 0.3s"
                  cursor="pointer"
                  onClick={() => setIsActive(!isActive)}
                  _hover={{
                    bg: isActive ? "green.600" : "gray.400"
                  }}
                  _after={{
                    content: '""',
                    position: "absolute",
                    width: "20px",
                    height: "20px",
                    borderRadius: "full",
                    bg: "white",
                    top: "2px",
                    left: isActive ? "22px" : "2px",
                    transition: "left 0.3s",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                  }}
                />
              </HStack>
              <Badge colorScheme="orange" px={3} py={1} rounded="full">
                <HStack gap={1}>
                  <Icon as={FiSave} />
                  <Text>Editing</Text>
                </HStack>
              </Badge>
            </HStack>
          </HStack>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
          {/* Main Form */}
          <VStack
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            gap={6}
            align="stretch"
          >
            {/* Basic Information */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiInfo} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Basic Information
                </Text>
              </HStack>
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Test Name *
                </Text>
                <Input
                  placeholder="e.g., Homepage Hero Test"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  size="lg"
                />
              </Box>

              <Box mt={6}>
                <Text fontWeight="medium" mb={2}>
                  Description
                </Text>
                <Textarea
                  placeholder="Describe what you're testing and why"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </Box>
            </Box>

            {/* Domain Selection */}
            <ABTestDomainSelection domain={domain} onChange={setDomain} />

            {/* Traffic Source Selection */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiLink} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Traffic Source
                </Text>
                <Badge colorScheme="red" size="sm">
                  Required
                </Badge>
              </HStack>

              <VStack gap={4} align="stretch">
                {/* Traffic Source Type Toggle */}
                <Box>
                  <HStack mb={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Where will your traffic come from?
                    </Text>
                  </HStack>
                  <HStack gap={2}>
                    <Button
                      size="sm"
                      variant={
                        trafficSource === "tracking-link" ? "solid" : "outline"
                      }
                      colorScheme={
                        trafficSource === "tracking-link" ? "blue" : "gray"
                      }
                      onClick={() => {
                        setTrafficSource("tracking-link");
                        setSelectedTrackingLinks([]);
                        setSelectedWebsitePath("");
                      }}
                    >
                      <HStack gap={1}>
                        <Icon as={FiLink} boxSize={4} />
                        <Text>Tracking Link</Text>
                      </HStack>
                    </Button>
                    <Button
                      size="sm"
                      variant={
                        trafficSource === "website-path" ? "solid" : "outline"
                      }
                      colorScheme={
                        trafficSource === "website-path" ? "blue" : "gray"
                      }
                      onClick={() => {
                        setTrafficSource("website-path");
                        setSelectedTrackingLinks([]);
                        setSelectedWebsitePath("");
                      }}
                    >
                      <HStack gap={1}>
                        <Icon as={FiNavigation} boxSize={4} />
                        <Text>Website Path</Text>
                      </HStack>
                    </Button>
                  </HStack>
                </Box>

                {/* Tracking Link Selection */}
                {trafficSource === "tracking-link" && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      Select Tracking Links
                    </Text>
                    <Box position="relative" data-traffic-source-dropdown>
                      <Box
                        as="button"
                        w="100%"
                        p={3}
                        bg="white"
                        border="1px solid"
                        borderColor={
                          trafficSourceDropdown.isOpen ? "blue.500" : "gray.300"
                        }
                        borderRadius="md"
                        textAlign="left"
                        fontSize="md"
                        onClick={(e) => {
                          if (isLoadingLinks) return;
                          e.preventDefault();
                          setTrafficSourceDropdown((prev) => ({
                            isOpen: !prev.isOpen,
                            searchQuery: ""
                          }));
                        }}
                        _hover={{ borderColor: "gray.400" }}
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                          outline: "none"
                        }}
                        _disabled={{
                          opacity: 0.6,
                          cursor: "not-allowed"
                        }}
                        aria-disabled={isLoadingLinks}
                      >
                        <HStack justify="space-between">
                          <VStack align="start" gap={0} flex={1}>
                            {selectedTrackingLinks.length > 0 ? (
                              <>
                                <Text
                                  color="black"
                                  fontSize="md"
                                  fontWeight="medium"
                                >
                                  {selectedTrackingLinks.length} tracking link
                                  {selectedTrackingLinks.length > 1
                                    ? "s"
                                    : ""}{" "}
                                  selected
                                </Text>
                                <Text
                                  color="gray.500"
                                  fontSize="sm"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                >
                                  {selectedTrackingLinks
                                    .map(
                                      (link) => link.linkName || "Unnamed Link"
                                    )
                                    .join(", ")}
                                </Text>
                              </>
                            ) : (
                              <Text color="gray.500" fontSize="md">
                                {isLoadingLinks
                                  ? "Loading links..."
                                  : "Select tracking links..."}
                              </Text>
                            )}
                          </VStack>
                          <Icon
                            as={FiChevronDown}
                            transform={
                              trafficSourceDropdown.isOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)"
                            }
                            transition="transform 0.2s"
                          />
                        </HStack>
                      </Box>

                      {trafficSourceDropdown.isOpen && !isLoadingLinks && (
                        <Box
                          position="absolute"
                          top="100%"
                          left={0}
                          right={0}
                          mt={1}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          shadow="lg"
                          zIndex={10}
                          maxH="400px"
                          overflow="hidden"
                        >
                          <Box
                            p={3}
                            borderBottom="1px solid"
                            borderColor="gray.200"
                          >
                            <HStack>
                              <Icon as={FiSearch} color="gray.400" />
                              <Input
                                placeholder="Search tracking links..."
                                value={trafficSourceDropdown.searchQuery}
                                onChange={(e) =>
                                  setTrafficSourceDropdown((prev) => ({
                                    ...prev,
                                    searchQuery: e.target.value
                                  }))
                                }
                                size="sm"
                                border="none"
                                _focus={{
                                  outline: "none",
                                  boxShadow: "none"
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </HStack>
                          </Box>

                          {/* Select/Clear All Actions */}
                          {!trafficSourceDropdown.searchQuery && (
                            <Box
                              p={3}
                              borderBottom="1px solid"
                              borderColor="gray.200"
                            >
                              <HStack justify="space-between">
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTrackingLinks([
                                      ...trackingLinks
                                    ]);
                                  }}
                                >
                                  Select All ({trackingLinks.length})
                                </Button>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="gray"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTrackingLinks([]);
                                  }}
                                >
                                  Clear All
                                </Button>
                              </HStack>
                            </Box>
                          )}

                          <Box maxH="300px" overflowY="auto">
                            {(() => {
                              const filteredTrackingLinks =
                                trackingLinks.filter((link) => {
                                  const searchQuery =
                                    trafficSourceDropdown.searchQuery.toLowerCase();
                                  return (
                                    (link.linkName
                                      ?.toLowerCase()
                                      .includes(searchQuery) ??
                                      false) ||
                                    (link.customId
                                      ?.toLowerCase()
                                      .includes(searchQuery) ??
                                      false) ||
                                    (link.platform
                                      ?.toLowerCase()
                                      .includes(searchQuery) ??
                                      false) ||
                                    (link.adSetName
                                      ?.toLowerCase()
                                      .includes(searchQuery) ??
                                      false) ||
                                    (link.adName
                                      ?.toLowerCase()
                                      .includes(searchQuery) ??
                                      false)
                                  );
                                });

                              return filteredTrackingLinks.length > 0 ? (
                                filteredTrackingLinks.map((link) => {
                                  const isSelected = selectedTrackingLinks.some(
                                    (selected) => selected.id === link.id
                                  );

                                  return (
                                    <Box
                                      key={link.id}
                                      p={3}
                                      bg={isSelected ? "blue.50" : "white"}
                                      _hover={{
                                        bg: isSelected ? "blue.100" : "gray.50"
                                      }}
                                      borderBottom="1px solid"
                                      borderColor="gray.100"
                                      cursor="pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTrackingLinkSelection(link);
                                      }}
                                    >
                                      <HStack align="start" gap={3}>
                                        <Box
                                          as="button"
                                          w="20px"
                                          h="20px"
                                          border="2px solid"
                                          borderColor={
                                            isSelected ? "blue.500" : "gray.300"
                                          }
                                          borderRadius="md"
                                          bg={isSelected ? "blue.500" : "white"}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          mt={0.5}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTrackingLinkSelection(link);
                                          }}
                                          _hover={{
                                            borderColor: isSelected
                                              ? "blue.600"
                                              : "gray.400"
                                          }}
                                        >
                                          {isSelected && (
                                            <Icon
                                              as={FiCheck}
                                              color="white"
                                              boxSize={3}
                                            />
                                          )}
                                        </Box>
                                        <VStack align="start" gap={0} flex={1}>
                                          <Text
                                            fontSize="md"
                                            fontWeight={
                                              isSelected ? "medium" : "normal"
                                            }
                                          >
                                            {link.linkName || "Unnamed Link"}
                                          </Text>
                                          <HStack>
                                            <Text
                                              fontSize="sm"
                                              color="gray.500"
                                              fontFamily="mono"
                                            >
                                              {link.customId || link.id}
                                            </Text>
                                            {link.platform && (
                                              <>
                                                <Text
                                                  fontSize="sm"
                                                  color="gray.400"
                                                >
                                                  •
                                                </Text>
                                                <Text
                                                  fontSize="sm"
                                                  color="gray.500"
                                                >
                                                  {link.platform}
                                                </Text>
                                              </>
                                            )}
                                          </HStack>
                                          {(link.adSetName || link.adName) && (
                                            <Text
                                              fontSize="sm"
                                              color="gray.500"
                                            >
                                              {[link.adSetName, link.adName]
                                                .filter(Boolean)
                                                .join(" • ")}
                                            </Text>
                                          )}
                                        </VStack>
                                      </HStack>
                                    </Box>
                                  );
                                })
                              ) : (
                                <Box p={4} textAlign="center">
                                  <Text fontSize="sm" color="gray.500">
                                    No tracking links found matching "
                                    {trafficSourceDropdown.searchQuery}"
                                  </Text>
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Select one or more tracking links that will direct users
                      to different test variants
                    </Text>
                  </Box>
                )}

                {/* Website Path Selection */}
                {trafficSource === "website-path" && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      Select Website Path to Test
                    </Text>
                    <HStack>
                      <Text fontSize="sm" color="gray.600" minW="fit-content">
                        https://{getMainDomain(domain)}
                      </Text>
                      <Box
                        position="relative"
                        flex={1}
                        data-website-path-dropdown
                      >
                        <Box
                          as="button"
                          w="100%"
                          p={3}
                          bg="white"
                          border="1px solid"
                          borderColor={
                            websitePathDropdown.isOpen ? "blue.500" : "gray.300"
                          }
                          borderRadius="md"
                          textAlign="left"
                          fontSize="md"
                          onClick={(e) => {
                            e.preventDefault();
                            setWebsitePathDropdown((prev) => ({
                              isOpen: !prev.isOpen,
                              searchQuery: ""
                            }));
                          }}
                          _hover={{ borderColor: "gray.400" }}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-blue-500)",
                            outline: "none"
                          }}
                        >
                          <HStack justify="space-between">
                            <Text
                              color={selectedWebsitePath ? "black" : "gray.500"}
                              fontSize="md"
                            >
                              {selectedWebsitePath ||
                                "Select a path to test..."}
                            </Text>
                            <Icon
                              as={FiChevronDown}
                              transform={
                                websitePathDropdown.isOpen
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)"
                              }
                              transition="transform 0.2s"
                            />
                          </HStack>
                        </Box>

                        {websitePathDropdown.isOpen && (
                          <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            mt={1}
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            shadow="lg"
                            zIndex={10}
                            maxH="400px"
                            overflow="hidden"
                          >
                            <Box
                              p={3}
                              borderBottom="1px solid"
                              borderColor="gray.200"
                            >
                              <HStack>
                                <Icon as={FiSearch} color="gray.400" />
                                <Input
                                  placeholder="Search paths..."
                                  value={websitePathDropdown.searchQuery}
                                  onChange={(e) =>
                                    setWebsitePathDropdown((prev) => ({
                                      ...prev,
                                      searchQuery: e.target.value
                                    }))
                                  }
                                  size="sm"
                                  border="none"
                                  _focus={{
                                    outline: "none",
                                    boxShadow: "none"
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </HStack>
                            </Box>

                            <Box maxH="300px" overflowY="auto">
                              {(() => {
                                const filteredWebsitePaths =
                                  availablePaths.filter((path) => {
                                    const searchQuery =
                                      websitePathDropdown.searchQuery.toLowerCase();
                                    return (
                                      path.value
                                        .toLowerCase()
                                        .includes(searchQuery) ||
                                      path.description
                                        .toLowerCase()
                                        .includes(searchQuery)
                                    );
                                  });

                                return filteredWebsitePaths.length > 0 ? (
                                  filteredWebsitePaths.map((path) => (
                                    <Box
                                      key={path.value}
                                      as="button"
                                      w="100%"
                                      p={3}
                                      textAlign="left"
                                      bg={
                                        selectedWebsitePath === path.value
                                          ? "blue.50"
                                          : "white"
                                      }
                                      _hover={{ bg: "gray.50" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedWebsitePath(path.value);
                                        setWebsitePathDropdown({
                                          isOpen: false,
                                          searchQuery: ""
                                        });
                                      }}
                                      borderBottom="1px solid"
                                      borderColor="gray.100"
                                    >
                                      <HStack
                                        justify="space-between"
                                        align="start"
                                      >
                                        <VStack align="start" gap={0}>
                                          <Text
                                            fontSize="md"
                                            fontWeight={
                                              selectedWebsitePath === path.value
                                                ? "medium"
                                                : "normal"
                                            }
                                          >
                                            {path.label}
                                          </Text>
                                          <Text fontSize="sm" color="gray.500">
                                            {path.description}
                                          </Text>
                                        </VStack>
                                        {selectedWebsitePath === path.value && (
                                          <Icon as={FiCheck} color="blue.500" />
                                        )}
                                      </HStack>
                                    </Box>
                                  ))
                                ) : (
                                  <Box p={4} textAlign="center">
                                    <Text fontSize="sm" color="gray.500">
                                      No paths found matching "
                                      {websitePathDropdown.searchQuery}"
                                    </Text>
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </HStack>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      When users visit this path, they will be randomly directed
                      to one of your test variants
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Test Variants */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiShuffle} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Test Variants
                </Text>
              </HStack>

              <Box>
                <HStack mb={3} justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    Test Variants
                  </Text>
                  <HStack gap={2}>
                    <Button
                      type="button"
                      size="xs"
                      colorScheme="gray"
                      variant="outline"
                      onClick={distributeEvenly}
                    >
                      Auto Distribute
                    </Button>
                    <Button
                      type="button"
                      size="xs"
                      colorScheme="blue"
                      onClick={addVariant}
                      disabled={variants.length >= 5}
                    >
                      <HStack gap={1}>
                        <Icon as={FiPlus} />
                        <Text>Add Variant</Text>
                      </HStack>
                    </Button>
                  </HStack>
                </HStack>

                <VStack gap={3} align="stretch">
                  {variants.map((variant, index) => {
                    const dropdown = dropdownStates[variant.id] || {
                      isOpen: false,
                      searchQuery: ""
                    };

                    const filteredPaths = availablePaths.filter(
                      (p) =>
                        p.value
                          .toLowerCase()
                          .includes(dropdown.searchQuery.toLowerCase()) ||
                        p.description
                          .toLowerCase()
                          .includes(dropdown.searchQuery.toLowerCase())
                    );
                    const filteredVariants = availableVariants.filter(
                      (v) =>
                        v.value
                          .toLowerCase()
                          .includes(dropdown.searchQuery.toLowerCase()) ||
                        v.description
                          .toLowerCase()
                          .includes(dropdown.searchQuery.toLowerCase())
                    );

                    return (
                      <Box
                        key={variant.id}
                        p={3}
                        bg="gray.50"
                        rounded="md"
                        border="1px"
                        borderColor="gray.200"
                      >
                        <HStack mb={2} justify="space-between">
                          <HStack>
                            <Text fontSize="sm" fontWeight="medium">
                              Variant {String.fromCharCode(65 + index)}
                            </Text>
                            <Badge
                              colorScheme={
                                ["green", "orange", "blue", "purple"][index % 4]
                              }
                              size="sm"
                            >
                              {variant.percentage}%
                            </Badge>
                          </HStack>
                          {variants.length > 2 && (
                            <Button
                              type="button"
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => removeVariant(variant.id)}
                            >
                              <Icon as={FiTrash2} />
                            </Button>
                          )}
                        </HStack>

                        {/* Type Selection */}
                        <HStack mb={2} flexWrap="wrap">
                          <Button
                            type="button"
                            size="xs"
                            variant={
                              variant.type === "path" ? "solid" : "outline"
                            }
                            colorScheme={
                              variant.type === "path" ? "blue" : "gray"
                            }
                            onClick={() =>
                              updateVariant(index, {
                                type: "path",
                                value: ""
                              })
                            }
                          >
                            <Icon as={FiLink} mr={1} boxSize={3} />
                            Website Path
                          </Button>
                          <Button
                            type="button"
                            size="xs"
                            variant={
                              variant.type === "variant" ? "solid" : "outline"
                            }
                            colorScheme={
                              variant.type === "variant" ? "blue" : "gray"
                            }
                            onClick={() =>
                              updateVariant(index, {
                                type: "variant",
                                value: ""
                              })
                            }
                          >
                            <Icon as={FiHash} mr={1} boxSize={3} />
                            Variant ID
                          </Button>
                        </HStack>

                        {/* Content based on type */}

                        {variant.type === "path" && (
                          <HStack>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              minW="fit-content"
                            >
                              https://{getMainDomain(domain)}
                            </Text>
                            <Box
                              position="relative"
                              flex={1}
                              data-ab-dropdown={variant.id}
                            >
                              <Box
                                as="button"
                                w="100%"
                                p={2}
                                bg="white"
                                border="1px solid"
                                borderColor={
                                  dropdown.isOpen ? "blue.500" : "gray.300"
                                }
                                borderRadius="md"
                                textAlign="left"
                                fontSize="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setDropdownStates((prev) => ({
                                    ...prev,
                                    [variant.id]: {
                                      isOpen: !dropdown.isOpen,
                                      searchQuery: ""
                                    }
                                  }));
                                }}
                                _hover={{ borderColor: "gray.400" }}
                                _focus={{
                                  borderColor: "blue.500",
                                  boxShadow:
                                    "0 0 0 1px var(--chakra-colors-blue-500)",
                                  outline: "none"
                                }}
                              >
                                <HStack justify="space-between">
                                  <Text
                                    color={variant.value ? "black" : "gray.500"}
                                    fontSize="sm"
                                  >
                                    {variant.value || "Select a path..."}
                                  </Text>
                                  <Icon
                                    as={FiChevronDown}
                                    transform={
                                      dropdown.isOpen
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)"
                                    }
                                    transition="transform 0.2s"
                                  />
                                </HStack>
                              </Box>

                              {dropdown.isOpen && (
                                <Box
                                  position="absolute"
                                  top="100%"
                                  left={0}
                                  right={0}
                                  mt={1}
                                  bg="white"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  shadow="lg"
                                  zIndex={10}
                                  maxH="300px"
                                  overflow="hidden"
                                >
                                  <Box
                                    p={2}
                                    borderBottom="1px solid"
                                    borderColor="gray.200"
                                  >
                                    <HStack>
                                      <Icon as={FiSearch} color="gray.400" />
                                      <Input
                                        placeholder="Search paths..."
                                        value={dropdown.searchQuery}
                                        onChange={(e) =>
                                          setDropdownStates((prev) => ({
                                            ...prev,
                                            [variant.id]: {
                                              ...dropdown,
                                              searchQuery: e.target.value
                                            }
                                          }))
                                        }
                                        size="sm"
                                        border="none"
                                        _focus={{
                                          outline: "none",
                                          boxShadow: "none"
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </HStack>
                                  </Box>

                                  <Box maxH="200px" overflowY="auto">
                                    {filteredPaths.length > 0 ? (
                                      filteredPaths.map((option) => (
                                        <Box
                                          key={option.value}
                                          as="button"
                                          w="100%"
                                          p={2}
                                          textAlign="left"
                                          bg={
                                            variant.value === option.value
                                              ? "blue.50"
                                              : "white"
                                          }
                                          _hover={{ bg: "gray.50" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateVariant(index, {
                                              value: option.value
                                            });
                                            setDropdownStates((prev) => ({
                                              ...prev,
                                              [variant.id]: {
                                                isOpen: false,
                                                searchQuery: ""
                                              }
                                            }));
                                          }}
                                          borderBottom="1px solid"
                                          borderColor="gray.100"
                                        >
                                          <HStack
                                            justify="space-between"
                                            align="start"
                                          >
                                            <VStack align="start" gap={0}>
                                              <Text
                                                fontSize="sm"
                                                fontWeight={
                                                  variant.value === option.value
                                                    ? "medium"
                                                    : "normal"
                                                }
                                              >
                                                {option.label}
                                              </Text>
                                              <Text
                                                fontSize="xs"
                                                color="gray.500"
                                              >
                                                {option.description}
                                              </Text>
                                            </VStack>
                                            {variant.value === option.value && (
                                              <Icon
                                                as={FiCheck}
                                                color="blue.500"
                                              />
                                            )}
                                          </HStack>
                                        </Box>
                                      ))
                                    ) : (
                                      <Box p={3} textAlign="center">
                                        <Text fontSize="sm" color="gray.500">
                                          No paths found matching "
                                          {dropdown.searchQuery}"
                                        </Text>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </HStack>
                        )}

                        {variant.type === "variant" && (
                          <Box
                            position="relative"
                            data-ab-dropdown={variant.id}
                          >
                            <Box
                              as="button"
                              w="100%"
                              p={2}
                              bg="white"
                              border="1px solid"
                              borderColor={
                                dropdown.isOpen ? "blue.500" : "gray.300"
                              }
                              borderRadius="md"
                              textAlign="left"
                              fontSize="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                setDropdownStates((prev) => ({
                                  ...prev,
                                  [variant.id]: {
                                    isOpen: !dropdown.isOpen,
                                    searchQuery: ""
                                  }
                                }));
                              }}
                              _hover={{ borderColor: "gray.400" }}
                              _focus={{
                                borderColor: "blue.500",
                                boxShadow:
                                  "0 0 0 1px var(--chakra-colors-blue-500)",
                                outline: "none"
                              }}
                            >
                              <HStack justify="space-between">
                                <Text
                                  color={variant.value ? "black" : "gray.500"}
                                  fontSize="sm"
                                  fontFamily={
                                    variant.value ? "mono" : "inherit"
                                  }
                                >
                                  {variant.value || "Select a variant..."}
                                </Text>
                                <Icon
                                  as={FiChevronDown}
                                  transform={
                                    dropdown.isOpen
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)"
                                  }
                                  transition="transform 0.2s"
                                />
                              </HStack>
                            </Box>

                            {dropdown.isOpen && (
                              <Box
                                position="absolute"
                                top="100%"
                                left={0}
                                right={0}
                                mt={1}
                                bg="white"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                shadow="lg"
                                zIndex={10}
                                maxH="300px"
                                overflow="hidden"
                              >
                                <Box
                                  p={2}
                                  borderBottom="1px solid"
                                  borderColor="gray.200"
                                >
                                  <HStack>
                                    <Icon as={FiSearch} color="gray.400" />
                                    <Input
                                      placeholder="Search variants..."
                                      value={dropdown.searchQuery}
                                      onChange={(e) =>
                                        setDropdownStates((prev) => ({
                                          ...prev,
                                          [variant.id]: {
                                            ...dropdown,
                                            searchQuery: e.target.value
                                          }
                                        }))
                                      }
                                      size="sm"
                                      border="none"
                                      _focus={{
                                        outline: "none",
                                        boxShadow: "none"
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </HStack>
                                </Box>

                                <Box maxH="200px" overflowY="auto">
                                  {filteredVariants.length > 0 ? (
                                    filteredVariants.map((option) => (
                                      <Box
                                        key={option.value}
                                        as="button"
                                        w="100%"
                                        p={2}
                                        textAlign="left"
                                        bg={
                                          variant.value === option.value
                                            ? "blue.50"
                                            : "white"
                                        }
                                        _hover={{ bg: "gray.50" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateVariant(index, {
                                            value: option.value
                                          });
                                          setDropdownStates((prev) => ({
                                            ...prev,
                                            [variant.id]: {
                                              isOpen: false,
                                              searchQuery: ""
                                            }
                                          }));
                                        }}
                                        borderBottom="1px solid"
                                        borderColor="gray.100"
                                      >
                                        <HStack
                                          justify="space-between"
                                          align="start"
                                        >
                                          <VStack align="start" gap={0}>
                                            <Text
                                              fontSize="sm"
                                              fontWeight={
                                                variant.value === option.value
                                                  ? "medium"
                                                  : "normal"
                                              }
                                              fontFamily="mono"
                                            >
                                              {option.label}
                                            </Text>
                                            <Text
                                              fontSize="xs"
                                              color="gray.500"
                                            >
                                              {option.description}
                                            </Text>
                                          </VStack>
                                          {variant.value === option.value && (
                                            <Icon
                                              as={FiCheck}
                                              color="blue.500"
                                            />
                                          )}
                                        </HStack>
                                      </Box>
                                    ))
                                  ) : (
                                    <Box p={3} textAlign="center">
                                      <Text fontSize="sm" color="gray.500">
                                        No variants found matching "
                                        {dropdown.searchQuery}"
                                      </Text>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}

                        <HStack mt={2} justify="space-between">
                          <Text fontSize="xs" color="gray.600">
                            Traffic Split
                          </Text>
                          <Input
                            type="number"
                            value={variant.percentage}
                            onChange={(e) =>
                              updateVariant(index, {
                                percentage: parseInt(e.target.value) || 0
                              })
                            }
                            size="sm"
                            width="80px"
                            min={0}
                            max={100}
                          />
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>

                {/* Traffic split validation */}
                {variants.reduce((sum, v) => sum + v.percentage, 0) !== 100 && (
                  <Box
                    mt={3}
                    p={3}
                    bg="yellow.50"
                    border="1px"
                    borderColor="yellow.300"
                    rounded="md"
                  >
                    <Text color="yellow.800" fontSize="sm">
                      ⚠️ Traffic split must equal 100%. Current total:{" "}
                      {variants.reduce((sum, v) => sum + v.percentage, 0)}%
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Scheduling */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiCalendar} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Schedule Test
                </Text>
              </HStack>

              <VStack gap={4} align="stretch">
                {/* Enable Scheduling Toggle */}
                <Box>
                  <HStack mb={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Auto-schedule test activation
                    </Text>
                    <Box
                      position="relative"
                      display="inline-block"
                      width="44px"
                      height="24px"
                      bg={schedule.enabled ? "blue.500" : "gray.300"}
                      borderRadius="full"
                      transition="all 0.3s"
                      cursor="pointer"
                      onClick={() =>
                        setSchedule((prev) => {
                          if (!prev.enabled) {
                            // Enabling: set start date to current time
                            return {
                              enabled: true,
                              startDate: getCurrentDateTimeString(),
                              endDate: ""
                            };
                          } else {
                            // Disabling: reset all dates
                            return {
                              enabled: false,
                              startDate: "",
                              endDate: ""
                            };
                          }
                        })
                      }
                      _hover={{
                        bg: schedule.enabled ? "blue.600" : "gray.400"
                      }}
                      _after={{
                        content: '""',
                        position: "absolute",
                        width: "20px",
                        height: "20px",
                        borderRadius: "full",
                        bg: "white",
                        top: "2px",
                        left: schedule.enabled ? "22px" : "2px",
                        transition: "left 0.3s",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                      }}
                    />
                  </HStack>
                  <Text fontSize="sm" color="gray.500">
                    Automatically start and stop the A/B test at specific times
                  </Text>
                </Box>

                {/* Schedule Configuration */}
                {schedule.enabled && (
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Start Date & Time
                      </Text>
                      <Input
                        type="datetime-local"
                        value={schedule.startDate}
                        onChange={(e) =>
                          setSchedule((prev) => ({
                            ...prev,
                            startDate: e.target.value
                          }))
                        }
                        size="md"
                        bg="white"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Test will automatically become active at this time
                      </Text>
                    </Box>
                    <Box>
                      <HStack mb={2}>
                        <Text fontSize="sm" fontWeight="medium">
                          End Date & Time
                        </Text>
                        <Badge colorScheme="gray" size="sm">
                          Optional
                        </Badge>
                      </HStack>
                      <Input
                        type="datetime-local"
                        value={schedule.endDate}
                        onChange={(e) =>
                          setSchedule((prev) => ({
                            ...prev,
                            endDate: e.target.value
                          }))
                        }
                        size="md"
                        bg="white"
                        placeholder="Leave empty to run indefinitely"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Leave empty to run indefinitely, or set end time for
                        automatic deactivation
                      </Text>
                    </Box>
                  </Grid>
                )}

                {/* Schedule Info */}
                {schedule.enabled && (
                  <Box
                    p={3}
                    bg="green.50"
                    rounded="md"
                    borderLeft="3px solid"
                    borderColor="green.400"
                  >
                    <HStack gap={2} align="start">
                      <Icon
                        as={FiInfo}
                        color="green.600"
                        boxSize={4}
                        mt={0.5}
                      />
                      <VStack align="start" gap={1}>
                        <Text
                          fontSize="sm"
                          color="green.800"
                          fontWeight="medium"
                        >
                          Automated Scheduling:
                        </Text>
                        <Text fontSize="sm" color="green.700">
                          • Test will automatically activate at the start time
                          (overriding manual active/inactive setting)
                        </Text>
                        <Text fontSize="sm" color="green.700">
                          • Test will automatically deactivate at the end time
                          (if specified)
                        </Text>
                        <Text fontSize="sm" color="green.700">
                          • Leave end date empty to run indefinitely until
                          manually stopped
                        </Text>
                        <Text fontSize="sm" color="green.700">
                          • You can still manually control the test before the
                          scheduled start time
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                )}

                {/* Schedule Validation */}
                {schedule.enabled &&
                  schedule.startDate &&
                  schedule.endDate &&
                  new Date(schedule.startDate) >=
                    new Date(schedule.endDate) && (
                    <Box
                      p={3}
                      bg="red.50"
                      rounded="md"
                      borderLeft="3px solid"
                      borderColor="red.400"
                    >
                      <HStack gap={2}>
                        <Icon as={FiInfo} color="red.600" boxSize={4} />
                        <Text fontSize="sm" color="red.800">
                          ⚠️ End date must be after start date
                        </Text>
                      </HStack>
                    </Box>
                  )}
              </VStack>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              disabled={
                !testName ||
                (trafficSource === "tracking-link" &&
                  selectedTrackingLinks.length === 0) ||
                (trafficSource === "website-path" && !selectedWebsitePath) ||
                variants.some((v) => !v.value) ||
                variants.reduce((sum, v) => sum + v.percentage, 0) !== 100 ||
                (schedule.enabled &&
                  (schedule.startDate === "" ||
                    (schedule.endDate !== "" &&
                      new Date(schedule.startDate) >=
                        new Date(schedule.endDate))))
              }
              width="full"
            >
              <HStack gap={2}>
                <Icon as={FiSave} />
                <Text>Save Changes</Text>
              </HStack>
            </Button>
          </VStack>

          {/* Sidebar */}
          <VStack gap={6} align="stretch">
            {/* Test Preview */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiShuffle} color="purple.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Test Preview
                </Text>
              </HStack>

              <VStack align="stretch" gap={3}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Test Name
                  </Text>
                  <Text fontSize="md" fontWeight="semibold">
                    {testName || "Untitled Test"}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Status
                  </Text>
                  <Badge colorScheme={isActive ? "green" : "gray"} size="md">
                    {isActive ? "Active" : "Inactive"}
                  </Badge>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Domain
                  </Text>
                  <Text fontSize="md" fontFamily="mono">
                    {domain}
                  </Text>
                </Box>

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.600"
                    mb={2}
                  >
                    Traffic Source
                  </Text>
                  <Box
                    p={2}
                    bg="blue.50"
                    rounded="md"
                    borderLeft="3px solid"
                    borderColor="blue.400"
                    mb={3}
                  >
                    <VStack align="start" gap={0}>
                      {trafficSource === "tracking-link" &&
                      selectedTrackingLinks.length > 0 ? (
                        <>
                          <Text fontSize="sm" fontWeight="medium">
                            {selectedTrackingLinks.length} Tracking Link
                            {selectedTrackingLinks.length > 1 ? "s" : ""}
                          </Text>
                          {selectedTrackingLinks.slice(0, 3).map((link) => (
                            <Text key={link.id} fontSize="xs" color="gray.500">
                              {link.linkName || "Unnamed Link"} •{" "}
                              {link.customId || link.id}
                            </Text>
                          ))}
                          {selectedTrackingLinks.length > 3 && (
                            <Text fontSize="xs" color="gray.500">
                              +{selectedTrackingLinks.length - 3} more...
                            </Text>
                          )}
                        </>
                      ) : trafficSource === "website-path" &&
                        selectedWebsitePath ? (
                        <>
                          <Text fontSize="sm" fontWeight="medium">
                            Website Path
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontFamily="mono"
                          >
                            https://{getMainDomain(domain)}
                            {selectedWebsitePath}
                          </Text>
                        </>
                      ) : trafficSource === "tracking-link" ? (
                        <Text fontSize="sm" color="gray.500">
                          No tracking links selected
                        </Text>
                      ) : trafficSource === "website-path" ? (
                        <Text fontSize="sm" color="gray.500">
                          No website path selected
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No source selected
                        </Text>
                      )}
                    </VStack>
                  </Box>

                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.600"
                    mb={2}
                  >
                    Variants ({variants.length})
                  </Text>
                  <VStack gap={2} align="stretch">
                    {variants.map((variant, index) => (
                      <Box key={variant.id} p={2} bg="gray.50" rounded="md">
                        <HStack justify="space-between" align="start">
                          <HStack align="start">
                            <Badge
                              colorScheme={
                                ["green", "orange", "blue", "purple"][index % 4]
                              }
                              size="sm"
                              mt={0.5}
                            >
                              {String.fromCharCode(65 + index)}
                            </Badge>
                            <VStack align="start" gap={0}>
                              {variant.type === "path" && variant.value ? (
                                <>
                                  <Text fontSize="sm" fontWeight="medium">
                                    Website Path
                                  </Text>
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    fontFamily="mono"
                                  >
                                    https://{getMainDomain(domain)}
                                    {variant.value}
                                  </Text>
                                </>
                              ) : variant.type === "variant" &&
                                variant.value ? (
                                <>
                                  <Text fontSize="sm" fontWeight="medium">
                                    Variant ID
                                  </Text>
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    fontFamily="mono"
                                  >
                                    {variant.value}
                                  </Text>
                                </>
                              ) : (
                                <Text fontSize="sm" color="gray.500">
                                  {variant.type === "path"
                                    ? "No path selected"
                                    : "No variant selected"}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            {variant.percentage}%
                          </Text>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>

                {variants.reduce((sum, v) => sum + v.percentage, 0) !== 100 && (
                  <Box
                    p={2}
                    bg="yellow.50"
                    rounded="md"
                    border="1px"
                    borderColor="yellow.200"
                  >
                    <Text fontSize="xs" color="yellow.700">
                      ⚠️ Traffic percentages don't equal 100%
                    </Text>
                  </Box>
                )}

                {/* Schedule Preview */}
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.600"
                    mb={2}
                  >
                    Schedule
                  </Text>
                  {schedule.enabled ? (
                    <VStack gap={2} align="stretch">
                      <Box p={2} bg="green.50" rounded="md">
                        <HStack justify="space-between" align="start">
                          <VStack align="start" gap={0}>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="green.800"
                            >
                              Automated Scheduling
                            </Text>
                            <Text fontSize="xs" color="green.600">
                              Start:{" "}
                              {schedule.startDate
                                ? new Date(schedule.startDate).toLocaleString()
                                : "Not set"}
                            </Text>
                            <Text fontSize="xs" color="green.600">
                              End:{" "}
                              {schedule.endDate
                                ? new Date(schedule.endDate).toLocaleString()
                                : "Run indefinitely"}
                            </Text>
                          </VStack>
                          <Icon as={FiCalendar} color="green.500" />
                        </HStack>
                      </Box>
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Manual control only
                    </Text>
                  )}
                </Box>
              </VStack>
            </Box>

            {/* Tips Card */}
            <Box
              bg="purple.50"
              shadow="sm"
              borderColor="purple.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="purple.600" boxSize={5} />
                <Text fontWeight="semibold" color="purple.900">
                  A/B Testing Tips
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="purple.800">
                <Text>
                  • Choose your traffic source first: tracking links compare
                  campaigns, website paths test destinations
                </Text>
                <Text>
                  • Use website paths or variant IDs for different experiences
                </Text>
                <Text>
                  • Run tests for at least 7-14 days for statistical
                  significance
                </Text>
                <Text>
                  • Ensure you have enough traffic for meaningful data
                </Text>
                <Text>• Document your hypothesis before starting</Text>
              </VStack>
            </Box>
          </VStack>
        </Grid>
      </Container>
    </Box>
  );
}

export default EditABTest;
